import React from 'react';
import { WidgetProps } from '@rjsf/utils';
import { Typography, makeStyles, createStyles, Link } from '@bb-ui/react-library';
import { useTranslation } from 'react-i18next';
import { HierarchyTenant } from 'App.types';
import { useDataSharingContext } from 'contexts/DataSharingContext';
import {
  DataExportStatus,
  isRegionMismatch,
  RegionMismatchWarning,
} from 'components/DataSharing/DataExportStatus';
import { getComparedRegion, LearnDataExport, ResourceRegion } from 'hooks/useLearnDataExport';

const useStyles = makeStyles((theme) =>
  createStyles({
    container: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
    label: {
      marginBottom: theme.spacing(1.5),
      fontSize: '0.875rem',
      fontWeight: 600,
    },
    requiredAsterisk: {
      color: theme.palette.error.main,
    },
    errorMessage: {
      marginBottom: theme.spacing(1),
      marginTop: theme.spacing(1),
    },
    table: {
      width: '100%',
      border: `1px solid ${theme.palette.divider}`,
      borderCollapse: 'collapse',
      borderRadius: theme.shape.borderRadius,
    },
    tableHeaderRow: {
      backgroundColor: theme.palette.background.default,
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    tableCell: {
      padding: theme.spacing(0.5, 1.5),
      textAlign: 'center',
    },
    tableCellLeft: {
      padding: theme.spacing(0.5, 1.5),
      textAlign: 'left',
      wordBreak: 'break-all',
    },
    tableHeaderCell: {
      padding: theme.spacing(0.5, 1.5),
      textAlign: 'left',
    },
    tableHeaderCellCenter: {
      padding: theme.spacing(0.5, 1.5),
      textAlign: 'center',
    },
    tableHeaderCellCheckbox: {
      padding: theme.spacing(0.5),
      textAlign: 'center',
      maxWidth: '75px',
    },
    tenantIdCell: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
    },
    tenantLink: {
      color: theme.palette.primary.main,
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
  }),
);

interface DataSharingRowProps {
  tenant: HierarchyTenant;
  isLinkedTenant: boolean;
  onDataSharingToggle: (tenantId: string) => void;
  requestingRegion?: string;
  /** Region of the tenant to compare with the requesting region */
  comparedRegion?: ResourceRegion;
  /** Only used for Learn tenants. Missing means still loading, null means the lookup failed. */
  learnDataExport?: LearnDataExport;
}

const DataSharingRow: React.FC<DataSharingRowProps> = ({
  tenant,
  isLinkedTenant,
  onDataSharingToggle,
  requestingRegion,
  comparedRegion,
  learnDataExport,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  // Data cannot be shared between regions. A tenant that is already linked
  // can still be unselected, so a wrong link can be removed.
  const disabled = !isLinkedTenant && isRegionMismatch(comparedRegion?.region, requestingRegion);

  return (
    <tr>
      <td className={classes.tableCellLeft}>
        <div className={classes.tenantIdCell}>
          <RegionMismatchWarning
            tenantId={tenant.id}
            tenantRegion={comparedRegion?.region}
            regionSource={comparedRegion?.source}
            requestingRegion={requestingRegion}
          />
          <Typography variant="body2">
            <Link
              href={`/tenants/${tenant.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className={classes.tenantLink}
            >
              {tenant.id}
            </Link>
          </Typography>
        </div>
      </td>
      <td className={classes.tableCell}>
        <Typography variant="body2">{tenant.tenantType || t('tenantInformation.unset')}</Typography>
      </td>
      <td className={classes.tableCell}>
        <Typography variant="body2">
          <DataExportStatus
            tenantType={tenant.tenantType}
            learnDataExport={learnDataExport}
            jobsLinkTenantId={tenant.id}
          />
        </Typography>
      </td>
      <td className={classes.tableCell}>
        <input
          type="checkbox"
          checked={isLinkedTenant}
          disabled={disabled}
          onChange={() => onDataSharingToggle(tenant.id)}
          aria-label={t('dataSharing.dataSharingAriaLabel', { tenantId: tenant.id })}
        />
      </td>
    </tr>
  );
};

/**
 * Custom RJSF widget for selecting tenants for data sharing.
 * This widget displays a table with checkboxes for data sharing selection.
 * It receives tenant hierarchy data from DataSharingContext.
 */
export const DataSharingPickerWidget: React.FC<WidgetProps> = ({
  id,
  value,
  onChange,
  required,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const {
    hierarchyTenants = [],
    setSelectedLinkedTenants,
    initialLinkedTenants,
    loading,
    fetchHierarchyData,
    requestingRegion,
    learnDataExport = {},
    learnResourceRegions = {},
  } = useDataSharingContext();

  // Use empty array as default if value is undefined/null
  const linkedTenants: string[] = React.useMemo(() => value ?? [], [value]);
  const [hasInitialized, setHasInitialized] = React.useState(false);

  // Fetch hierarchy data on mount
  React.useEffect(() => {
    fetchHierarchyData();
  }, [fetchHierarchyData]);

  // Prefill values if something comes already selected from the api
  // but do it only once
  React.useEffect(() => {
    if (!loading && !hasInitialized && initialLinkedTenants && initialLinkedTenants.length > 0) {
      onChange(initialLinkedTenants);
      setHasInitialized(true);
    }
  }, [loading, hasInitialized, linkedTenants, initialLinkedTenants, onChange]);

  // Sync the form value with the context
  // necessary to keep the sync with reporting tenant widget
  React.useEffect(() => {
    if (setSelectedLinkedTenants) {
      setSelectedLinkedTenants(linkedTenants);
    }
  }, [linkedTenants, setSelectedLinkedTenants]);

  const handleDataSharingToggle = (childId: string) => {
    const isSelected = linkedTenants.includes(childId);

    let newLinkedTenants: string[];
    if (isSelected) {
      newLinkedTenants = linkedTenants.filter((id) => id !== childId);
    } else {
      newLinkedTenants = [...linkedTenants, childId];
    }

    onChange(newLinkedTenants);
  };

  return (
    <div className={classes.container} id={id}>
      <Typography variant="h6" className={classes.label}>
        {required && <span className={classes.requiredAsterisk}>* </span>}
        {t('dataSharing.linkedTenants')}
      </Typography>
      {loading && (
        <Typography variant="body2" color="textSecondary">
          {t('dataSharing.loadingTenants')}
        </Typography>
      )}
      {!loading && hierarchyTenants.length === 0 && (
        <Typography variant="body2" color="textSecondary">
          {t('dataSharing.noLinkableTenants')}
        </Typography>
      )}
      {!loading && hierarchyTenants.length > 0 && (
        <table className={classes.table}>
          <thead>
            <tr className={classes.tableHeaderRow}>
              <th className={classes.tableHeaderCell}>
                <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                  {t('dataSharing.tenantId')}
                </Typography>
              </th>
              <th className={classes.tableHeaderCellCenter}>
                <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                  {t('dataSharing.type')}
                </Typography>
              </th>
              <th className={classes.tableHeaderCellCenter}>
                <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                  {t('dataSharing.dataExport')}
                </Typography>
              </th>
              <th className={classes.tableHeaderCellCheckbox}>
                <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                  {t('dataSharing.dataSharing')}
                </Typography>
              </th>
            </tr>
          </thead>
          <tbody>
            {hierarchyTenants.map((tenant: HierarchyTenant) => (
              <DataSharingRow
                key={tenant.id}
                tenant={tenant}
                isLinkedTenant={linkedTenants.includes(tenant.id)}
                onDataSharingToggle={handleDataSharingToggle}
                requestingRegion={requestingRegion}
                comparedRegion={getComparedRegion(tenant, learnResourceRegions)}
                learnDataExport={learnDataExport[tenant.id]}
              />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};
